<template>
  <zm-dialog
    v-model="visible"
    ref="portValidateDialog"
    aria-label="port number validate dialog"
    class="port-number-validate opc-full-zm-dialog"
    width="544px"
  >
  <template #header>
      <h1 class="port-number-validate__title">
        {{ $t('billing.pbx.port_number.title') }}
        <zm-tooltip
          v-if="is_desktop_reactive || is_tablet_reactive"
          placement="bottom"
          :content="$t('billing.pbx.port_number.guarantee_tips')"
        >
          <tips-icon aria-label="validate port number" />
        </zm-tooltip>
      </h1>
    </template>
    <h2 v-if="!(is_desktop_reactive || is_tablet_reactive)" class="port-number-validate__guarantee-tips">
      {{ $t('billing.pbx.port_number.guarantee_tips') }}</h2>
    <div class="port-number-validate__content">
      <zm-form @submit.prevent>
        <zm-form-item :error="errorMessage" >
          <div class="port-number-validate__input-wrapper">
            <zm-input
              v-model="form.portNumber"
              data-testid="checkout-input-185"
              label="Phone number"
              pass-by-max-length
              :disabled="loading"
              :maxlength="14"
              :placeholder="$t('billing.pbx.port_number.placeholder')"
              class="port-number-validate__input"
              size="lg"
              @enter.prevent="validate"
            >
            </zm-input>
            <zm-button
              data-testid="checkout-button-186"
              :disabled="validateDisabled"
              :loading="loading"
              class="port-number-validate__btn"
              type="text"
              @click="validate"
            >
              {{ $t('billing.common.validate') }}
            </zm-button>
          </div>
        </zm-form-item>
      </zm-form>
      <port-number-checked-list
        v-if="checkPortNumberCount > 0"
        :port-number-list="checkedNumberList"
        :portNumberShowClear="portNumberShowClear"
        @removeNumber="removeNumber"
      />
      <max-phone-number-warning v-if="maxNumberError" key="maxNumberError"
                                :max-select-phone-numbers="maxSelectPhoneNumbers" />
      <template v-else-if="maxLicenseError">
        <zm-banner
          v-if="isZoneProPlus"
          key="maxLicenseError-pro-plus"
          type="warning">
          <div>
            <p>{{$t('billing.zone_pro_plus.max_user_tips', [maxLicenses])}}</p>
            <zm-button data-testid="checkout-button-187" type="text" @click="handleExploreOtherPlans">
              {{ $t('billing.zone_pro_plus.explore_other_plans_cta') }}
            </zm-button>
          </div>
        </zm-banner>
        <zm-banner
          v-else
          key="maxLicenseError"
          type="warning">
          <p v-html="$t('billing.pbx.more_than_license', [maxLicenses, pbxContactSalesLink])" />
        </zm-banner>
      </template>
      <zm-banner
        v-if="showSuccess"
        key="successful"
        type="success"
      >
        <span v-html="successTipMsg"></span>
      </zm-banner>
      <p v-else-if="showError"
         class="port-number-validate__content--verify-error-msg"
         v-html="$t('billing.pbx.port_number.verify_fail', [pbxContactSalesLink])" />
    </div>
    <template #footer>
      <phone-select-footer
        :cancel-text="$t('common.btn_cancel')"
        :disabled-save="disabledSave"
        :on-cancel="onCancel"
        :on-save="onSave"
        :save-text="$t('billing.continue')"
        class="port-number-validate__footer"
      />
    </template>
  </zm-dialog>
</template>

<script>
import { isEqual, uniq, noop } from 'lodash';
import { checkPortNumber } from '@/plans/ZOOM_PHONE/helper/validate';
import { isEmptyVal } from '@/utils';
import { formatPortNumber, getCurrentTrackingPlanName, parsePortNumber } from '@/plans/ZOOM_PHONE/helper/utils';
import phoneNumberApiMixin from '@/plans/ZOOM_PHONE/mixin/phone-number-api';
import PortNumberCheckedList from '@/plans/ZOOM_PHONE/component/port-number-checked-list';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import MaxPhoneNumberWarning from '@/plans/ZOOM_PHONE/component/max-phone-number-warning';
import computedMixin from '@/plans/ZOOM_PHONE/mixin/computed';
import { maxSelectPhoneNumbers } from '@/plans/ZOOM_PHONE/helper/constants';
import { getPbxContactSalesLink } from '@/utils/i18n-shortcuts';
import PhoneSelectFooter from './phone-select-footer';
import { computedPbxMeteredPlans, computedUnlimitedCallingPlans } from '@/common/service/computers/plans/pbx';
import { mapGetters } from 'vuex';
import { logger } from '@/utils/log';

const blockCaseMap = {
  maxLicense: 'MAX-LICENSE-99',
  maxNumber: 'MAX-number-10',
};

export default {
  name: 'ValidatePortNumberDialog',
  components: { MaxPhoneNumberWarning, PortNumberCheckedList, PhoneSelectFooter },
  mixins: [ConfirmComponent, phoneNumberApiMixin, computedMixin],
  data() {
    return {
      form: {
        portNumber: '',
      },
      error: {
        frontError: '',
        backError: '',
      },
      checkedNumberList: [],
      originPortNumberList: [],
      localCount: 0,
      tollFreeCount: 0,
      portCount: 0,
      // true means phone number not required check
      // false means phone number should required check
      clearable: true,
      pbxContactSalesLink: getPbxContactSalesLink(),
      maxSelectPhoneNumbers: maxSelectPhoneNumbers,
      maxLicenses: 99,
      handleExploreOtherPlans: noop,
      isZoneProPlus: false,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans']),
    trackingPlanName() {
      return getCurrentTrackingPlanName();
    },
    maxLicenseError() {
      return this.blockValidateNumber === blockCaseMap.maxLicense;
    },
    maxNumberError() {
      return this.blockValidateNumber === blockCaseMap.maxNumber;
    },
    blockValidateNumber() {
      const otherNumbersTotal = this.localCount + this.tollFreeCount;

      // todo just port number
      const checkedNumbersCount = this.checkedNumberList?.length || 0;
      const totalCount = otherNumbersTotal + checkedNumbersCount;

      if (totalCount >= this.maxLicenses) {
        return blockCaseMap.maxLicense;
      } else if (checkedNumbersCount >= this.maxSelectPhoneNumbers) {
        return blockCaseMap.maxNumber;
      }

      return '';
    },
    errorMessage() {
      return this.error.frontError || this.error.backError;
    },
    showError() {
      return !isEmptyVal(this.form.portNumber) && this.error.backError;
    },
    showSuccess() {
      const originPortNumberList = this.originPortNumberList || [];
      const checkedNumberCount = this.checkedNumberList?.length;

      const basicValidate = checkedNumberCount > 0 && isEmptyVal(this.error.backError);

      if (originPortNumberList?.length) {
        return basicValidate && !isEqual(this.originPortNumberList, this.checkedNumberList);
      }

      return basicValidate;
    },
    validateDisabled() {
      return isEmptyVal(this.form.portNumber) || !isEmptyVal(this.blockValidateNumber);
    },
    checkPortNumberCount() {
      return this.checkedNumberList?.length || 0;
    },
    disabledSave() {
      // &. is validating port number
      // &. have error message for validate port number
      // 1. have selected local number or toll-free number
      // 2. have selected port number is empty
      // 3. current checked port number is empty
      return this.loading
        || isEqual(this.originPortNumberList, this.checkedNumberList)
        || (isEmptyVal(this.errorMessage) === false && this.checkPortNumberCount === 0)
        || (this.clearable && this.checkPortNumberCount === 0 && this.portCount === 0);
    },
    portNumberShowClear() {
      return this.clearable ? true : (this.checkPortNumberCount === 1 ? false : true);
    },
    successTipMsg() { // just apply for US/CA region, users can only choose one type of calling plan, either unlimited or metered
      const unlimitedPlans = computedUnlimitedCallingPlans(this.getSelectedPlans);

      if (unlimitedPlans?.length) {
        return this.$t('billing.pbx.port_number.verify_successful');
      }

      const meteredPlans = computedPbxMeteredPlans(this.getSelectedPlans);

      if (meteredPlans?.length) {
        return this.$t('billing.pbx.port_number.verify_successful_metered');
      }

      return '';
    },
  },
  methods: {
    parsePortNumber(number) {
      return parsePortNumber(number);
    },
    resetPortNumberForm() {
      this.form.portNumber = '';
      this.error = { frontError: '', backError: '' };
    },
    async validate() {
      if (this.validateDisabled) return;

      const portNumber = this.form.portNumber;
      const errorMsg = checkPortNumber(formatPortNumber(portNumber), this.checkedNumberList, portNumber);
      this.error.frontError = checkPortNumber(formatPortNumber(portNumber), this.checkedNumberList, portNumber);
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - port your numbers - validate`);
      if (errorMsg) return;

      const phoneNumber = formatPortNumber(this.form.portNumber);
      const backMessage = await this.validatePortNumbers(phoneNumber);

      if (isEmptyVal(backMessage)) {
        this.form.portNumber = '';
        this.checkedNumberList = uniq([...this.checkedNumberList, phoneNumber]);
      }

      this.error.backError = backMessage;
    },
    removeNumber(index) {
      this.checkedNumberList.splice(index, 1);
    },
    onSave() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - port your numbers - save`);
      return this.handleSure(this.checkedNumberList);
    },
    onCancel() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - port your numbers - cancel`);
      return this.handleCancel('close validate port number dialog').catch((error) => {
        logger.warn('handle cancel error or closed the validate port number dialog, error: ', error);
      });
    },
  },
  watch: {
    visible: {
      handler(val) {
        !val && this.resetPortNumberForm();
      },
    },
    errorMessage: {
      handler(message) {
        message && this.$tracking.buyInteraction(
          `${this.trackingPlanName} numbers`,
          `${this.callingPlanName} - port your numbers - error - ${message}`,
        );
      },
    },
    'form.portNumber': {
      handler(value, oldValue) {
        const regex = /^[\d-()\s]+$/g;

        if (isEmptyVal(value) || regex.test(value)) {
          this.form.portNumber = parsePortNumber(value);
        } else {
          this.form.portNumber = oldValue;
        }

        this.error.backError = '';
      },
    },
  },
  mounted() {
    this.$refs?.portValidateDialog?.$el?.addEventListener('click', (e) => {
      if (e?.target?.className === 'port-number-verify-fail') {
        this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - port your numbers - error - contact sales`);
      }
    });
  },
};
</script>

<style lang="scss">
@import '@/styles/scss-utils.scss';
@import "../style/dialog.m.scss";
@import '@zoom/billing-responsive/styles/responsive.scss';

.port-number-validate {

  &__input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__footer {
    padding-top: 24px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
  }

  &__content {
    .zbo-form-item__label-wrapper {
      display: none;
    }
    .zbo-banner {
      margin-top: 16px;
    }
    &--verify-error-msg {
      font-size: 14px;
      margin-top: 16px;
      line-height: 18px;
    }
  }

  @include media(mobile) {
    .zbo-dialog {
      &__footer {
        padding-top: 12px;

        .port-number-validate {
          &__footer {
            padding-top: 12px;
            display: flex;
            justify-content: center;

            .zbo-button {
              flex: 1;
            }
          }
        }
      }
    }

    &__title, &__content {
      padding-left: 0px;
      padding-right: 0px;
    }

    &__guarantee-tips {
      color: #515079;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 21px */
      padding-bottom: 16px;
    }
  }
}
</style>
